/**
 * ConfirmGroupDeliveryDialog
 * Confirms delivery for ALL pending items in a PO group at once.
 * Supports multiple lot entries per item. Sets delivery_status='delivered'.
 */
import { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Chip, Divider,
  CircularProgress, Alert, alpha, useTheme, IconButton, LinearProgress,
  Grid, TextField, List, ListItem, ListItemButton, ListItemText,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Close as CloseIcon, CheckCircle as CheckCircleIcon,
  UploadFile as UploadIcon, Add as AddIcon, Delete as DeleteIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { getInventoryConfig } from '../../inventory/inventory-components/shared/inventoryConfig';
import { confirmPODelivery } from './poTransactionHelpers';
import { supabase } from '../../../supabaseClient';

const TYPE_COLORS = {
  products: '#1565c0', chemicals: '#7b1fa2', caps: '#00796b',
  bottles:  '#2e7d32', labels:    '#e65100', supplies: '#5d4037',
};

const TYPE_LABELS = {
  products: 'Products', chemicals: 'Chemicals', caps: 'Caps',
  bottles: 'Bottles', labels: 'Labels', supplies: 'Supplies',
};

const BASE_DOCS = [
  { key: 'invoice', label: 'Invoice' },
  { key: 'coa',     label: 'COA' },
  { key: 'msds',    label: 'MSDS' },
  { key: 'bol',     label: 'Bill of Lading' },
];
const PRODUCT_EXTRA_DOCS = [
  { key: 'sds',            label: 'SDS' },
  { key: 'production_doc', label: 'Production Document' },
];

const tfSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.95)',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.primary.main, 0.25) },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
  },
  '& .MuiInputLabel-root': { fontWeight: 500 },
});

function getItemName(tx) {
  return tx.products?.name || tx.raw_materials?.name || tx.caps?.name ||
         tx.bottles?.name  || tx.labels?.name        || tx.supplies?.name || '—';
}

function getOriginalQty(tx) {
  if (tx._type === 'chemicals') return tx.unit_quantity ?? tx.total_quantity ?? 0;
  if (tx._type === 'products')  return tx.quantity_change ?? tx.total_base_units ?? 0;
  return tx.quantity ?? 0;
}

function initLot(tx) {
  const base = {
    lotNumber:   tx.lot_number   || '',
    batchNumber: tx.batch_number || '',
    quantity:    String(getOriginalQty(tx)),
    expiryDate:  '',
  };
  if (tx._type === 'products') {
    const uQty          = tx.input_units   || 0;
    const cQty          = tx.input_cases   || 0;
    const pQty          = tx.input_pallets || 0;
    const upc           = tx.units_per_case   || 1;
    const cpp           = tx.cases_per_pallet || 1;
    const hasConfig     = upc > 1 || cQty > 0 || pQty > 0 || uQty > 0;
    const enableCases   = cQty > 0 || upc > 1;
    const enablePallets = pQty > 0;
    const configName    = hasConfig
      ? `${upc} units/case${enablePallets ? ` · ${cpp} cases/pallet` : ''}`
      : '';
    return {
      ...base,
      unitQty:         uQty > 0 ? String(uQty) : '',
      caseQty:         cQty > 0 ? String(cQty) : '',
      palletQty:       pQty > 0 ? String(pQty) : '',
      mfgDate:         '',
      _configId:       hasConfig ? 'prefilled' : '',
      _configName:     configName,
      _unitsPerCase:   upc,
      _casesPerPallet: cpp,
      _enableCases:    enableCases,
      _enablePallets:  enablePallets,
    };
  }
  return base;
}

function DocUploadField({ docKey, label, file, onChange }) {
  const theme = useTheme();
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      <Box
        component="label"
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: 2, cursor: 'pointer',
          border: `1px dashed ${file ? alpha(theme.palette.success.main, 0.5) : alpha(theme.palette.grey[400], 0.6)}`,
          bgcolor: file ? alpha(theme.palette.success.main, 0.04) : alpha(theme.palette.grey[50], 0.8),
          '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.03) },
        }}
      >
        <input type="file" hidden onChange={e => onChange(docKey, e.target.files[0] || null)} />
        <UploadIcon sx={{ fontSize: 18, color: file ? 'success.main' : 'text.disabled' }} />
        <Typography variant="caption" sx={{ color: file ? 'success.main' : 'text.secondary', fontWeight: file ? 500 : 400, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file ? file.name : 'Click to upload...'}
        </Typography>
        {file && (
          <IconButton size="small" onClick={e => { e.preventDefault(); onChange(docKey, null); }}
            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

export default function ConfirmGroupDeliveryDialog({ open, onClose, transactions = [], companyId, onSuccess }) {
  const theme = useTheme();
  const today = new Date().toISOString().split('T')[0];

  const [actualDate,    setActualDate]    = useState(today);
  const [invoiceNumber, setInvoiceNumber] = useState(() => transactions[0]?.invoice_number || '');
  const [invoiceDate,   setInvoiceDate]   = useState(() => transactions[0]?.invoice_date   || '');
  const [taxAmount,     setTaxAmount]     = useState('');
  const [invoiceDueDate, setInvoiceDueDate] = useState('');
  const [shippingCost,  setShippingCost]  = useState('');
  const [documents,     setDocuments]     = useState({});
  // itemLots: { [tx.id]: Array<{ lotNumber, batchNumber, quantity, expiryDate }> }
  const [itemLots,      setItemLots]      = useState(() => {
    const init = {};
    transactions.filter(t => t.delivery_status !== 'delivered').forEach(tx => {
      init[tx.id] = [initLot(tx)];
    });
    return init;
  });
  const [submitting,    setSubmitting]    = useState(false);
  const [progress,      setProgress]      = useState(0);
  const [error,         setError]         = useState(null);
  const [autoPOFile,    setAutoPOFile]    = useState(null);
  const [unitConfigs,       setUnitConfigs]       = useState([]);
  const [configDialogOpen,  setConfigDialogOpen]  = useState(false);
  const [configDialogTarget,setConfigDialogTarget]= useState(null); // {txId, lotIdx}
  const [configSearch,      setConfigSearch]      = useState('');
  const [selectedVariants,  setSelectedVariants]  = useState({}); // { [txId]: variantId }
  const [variantOptions,    setVariantOptions]    = useState({}); // { [txId]: [{id,name}] }

  const pendingItems = transactions.filter(t => t.delivery_status !== 'delivered');
  const poNumber     = transactions[0]?.po_number || '—';

  // Re-initialize all form state when dialog opens (transactions may have changed)
  useEffect(() => {
    if (open) {
      setInvoiceNumber(transactions[0]?.invoice_number || '');
      setInvoiceDate(transactions[0]?.invoice_date   || '');
      const init = {};
      transactions.filter(t => t.delivery_status !== 'delivered').forEach(tx => {
        init[tx.id] = [initLot(tx)];
      });
      setItemLots(init);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch unit configurations for product lot entries
  useEffect(() => {
    if (!open) return;
    if (!transactions.some(t => t._type === 'products')) return;
    supabase.from('unit_configurations').select('*')
      .eq('company_id', companyId).eq('is_active', true).order('name')
      .then(({ data }) => setUnitConfigs(data || []));
  }, [open, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch product variants for each product transaction
  useEffect(() => {
    if (!open) return;
    const productTxs = transactions.filter(t => t._type === 'products' && t.product_id);
    if (!productTxs.length) return;
    const uniqueProductIds = [...new Set(productTxs.map(t => t.product_id))];
    supabase.from('product_variants').select('id, name, product_id')
      .in('product_id', uniqueProductIds).eq('company_id', companyId)
      .then(({ data }) => {
        const opts = {};
        productTxs.forEach(tx => {
          opts[tx.id] = (data || []).filter(v => v.product_id === tx.product_id);
        });
        setVariantOptions(opts);
        // Pre-select if transaction already has a variant_id
        const preSelected = {};
        productTxs.forEach(tx => { if (tx.variant_id) preSelected[tx.id] = tx.variant_id; });
        setSelectedVariants(preSelected);
      });
  }, [open, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch vendor PO and generate PDF matching the formal PO format
  useEffect(() => {
    if (!open || !poNumber || poNumber === '—') return;
    let cancelled = false;

    (async () => {
      try {
        const [{ data: po }, { data: company }] = await Promise.all([
          supabase.from('vendor_PO').select('*, suppliers(name, email)').eq('company_id', companyId).eq('vendor_PO_number', poNumber).maybeSingle(),
          supabase.from('companies').select('name, address, city, state, zip_code, country, phone, email, website').eq('id', companyId).single(),
        ]);
        if (!po || cancelled) return;

        const { data: items } = await supabase.from('vendor_PO_items').select('*').eq('vendor_PO_id', po.id);

        const fmtDate = (d) => {
          if (!d) return 'N/A';
          const p = d.split('T')[0].split('-');
          return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : d;
        };
        const fmtCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
        const fmtNumber   = (n) => new Intl.NumberFormat('en-US').format(n || 0);

        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
        const pageWidth  = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 40;
        let y = margin;

        // Title
        doc.setFontSize(24); doc.setFont('helvetica', 'normal');
        doc.text('Purchase Order', margin, y); y += 25;

        // PO details (left)
        doc.setFontSize(10); doc.setTextColor(100, 100, 100);
        doc.text(`PO #: ${po.vendor_PO_number || 'N/A'}`, margin, y); y += 14;
        doc.text(`Date: ${fmtDate(po.vendor_PO_date)}`, margin, y);
        if (po.expected_delivery_date) { y += 14; doc.text(`Expected Delivery: ${fmtDate(po.expected_delivery_date)}`, margin, y); }

        // Company info (right)
        doc.setTextColor(0, 0, 0); doc.setFontSize(12); doc.setFont('helvetica', 'bold');
        doc.text(company?.name || 'Your Company', pageWidth - margin, margin, { align: 'right' });
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
        let cy = margin + 16;
        const companyLines = [
          company?.address,
          [company?.city, company?.state, company?.zip_code].filter(Boolean).join(', '),
          company?.country,
          company?.phone ? `Phone: ${company.phone}` : null,
          company?.email ? `Email: ${company.email}` : null,
          company?.website ? `Web: ${company.website}` : null,
        ].filter(Boolean);
        companyLines.forEach(line => { doc.text(line, pageWidth - margin, cy, { align: 'right' }); cy += 12; });

        y = Math.max(y, cy) + 30;

        // Ship To & Vendor
        doc.setFontSize(11); doc.setFont('helvetica', 'bold');
        doc.text('Ship To:', margin, y);
        if (po.suppliers?.name) doc.text('Vendor:', pageWidth / 2 + 20, y);
        y += 14; doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
        const shipParts = [po.shipping_street, po.shipping_city, po.shipping_state, po.shipping_zip, po.shipping_country].filter(Boolean);
        const shipText = shipParts.join(', ') || 'No shipping address';
        const shipLines = doc.splitTextToSize(shipText, (pageWidth / 2) - margin - 30);
        const shipStartY = y;
        shipLines.forEach(l => { doc.text(l, margin, y); y += 12; });
        if (po.suppliers?.name) {
          doc.text(po.suppliers.name, pageWidth / 2 + 20, shipStartY);
          if (po.suppliers.email) doc.text(`Email: ${po.suppliers.email}`, pageWidth / 2 + 20, shipStartY + 12);
        }
        y += 20;

        // Line items table
        if (items?.length) {
          const colWidths = [140, 180, 60, 70, 70];
          const headers   = ['Item Name', 'Description', 'Quantity', 'Unit Price', 'Amount'];
          doc.setFillColor(245, 245, 245);
          doc.rect(margin, y, pageWidth - margin * 2, 20, 'F');
          doc.setDrawColor(224, 224, 224);
          doc.rect(margin, y, pageWidth - margin * 2, 20, 'S');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
          let xPos = margin + 5;
          headers.forEach((h, i) => {
            const align = i >= 2 ? 'right' : 'left';
            doc.text(h, align === 'right' ? xPos + colWidths[i] - 10 : xPos, y + 14, { align });
            xPos += colWidths[i];
          });
          y += 20;
          doc.setFont('helvetica', 'normal');
          items.forEach(item => {
            if (y > pageHeight - margin - 50) { doc.addPage(); y = margin; }
            const nameLines = doc.splitTextToSize(item.item_name || 'N/A', colWidths[0] - 10);
            const descLines = doc.splitTextToSize(item.description || '-', colWidths[1] - 10);
            const rowH = Math.max(18, Math.max(nameLines.length, descLines.length) * 11 + 6);
            doc.setDrawColor(224, 224, 224);
            doc.rect(margin, y, pageWidth - margin * 2, rowH, 'S');
            xPos = margin + 5;
            doc.text(nameLines, xPos, y + 10); xPos += colWidths[0];
            doc.text(descLines, xPos, y + 10); xPos += colWidths[1];
            doc.text(fmtNumber(item.quantity || 0), xPos + colWidths[2] - 10, y + 10, { align: 'right' }); xPos += colWidths[2];
            doc.text(fmtCurrency(item.unit_price || 0), xPos + colWidths[3] - 10, y + 10, { align: 'right' }); xPos += colWidths[3];
            doc.text(fmtCurrency(item.total_price || 0), xPos + colWidths[4] - 10, y + 10, { align: 'right' });
            y += rowH;
          });
          y += 10;
          const total = items.reduce((s, i) => s + (parseFloat(i.total_price) || 0), 0);
          doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
          doc.setDrawColor(0, 0, 0); doc.setLineWidth(1.5);
          doc.line(pageWidth - margin - 150, y, pageWidth - margin, y);
          doc.setLineWidth(0.5); y += 16;
          doc.text('Total:', pageWidth - margin - 150, y);
          doc.text(fmtCurrency(total), pageWidth - margin, y, { align: 'right' });
        }

        y += 30;

        // Payment Terms
        doc.setFontSize(10);
        if (po.payment_terms) {
          if (y > 680) { doc.addPage(); y = margin; }
          doc.setFont('helvetica', 'bold');
          doc.text('Payment Terms:', margin, y); y += 14;
          doc.setFont('helvetica', 'normal');
          doc.text(`Payment Due: ${po.payment_terms}`, margin, y); y += 20;
        }

        // Notes
        if (po.notes) {
          if (y > 680) { doc.addPage(); y = margin; }
          doc.setFont('helvetica', 'bold');
          doc.text('Notes:', margin, y); y += 14;
          doc.setFont('helvetica', 'normal');
          const noteLines = doc.splitTextToSize(po.notes, pageWidth - margin * 2);
          doc.text(noteLines, margin, y); y += noteLines.length * 12 + 10;
        }

        // Special Instructions
        if (po.special_instructions) {
          if (y > 680) { doc.addPage(); y = margin; }
          doc.setFont('helvetica', 'bold');
          doc.text('Special Instructions:', margin, y); y += 14;
          doc.setFont('helvetica', 'normal');
          const instrLines = doc.splitTextToSize(po.special_instructions, pageWidth - margin * 2);
          doc.text(instrLines, margin, y); y += instrLines.length * 12 + 10;
        }

        // Terms & Conditions
        if (po.terms_and_conditions) {
          const lineHeight = 11;
          const boxPadding = 10;
          doc.setFontSize(8);
          const termsLines = doc.splitTextToSize(po.terms_and_conditions, pageWidth - margin * 2 - boxPadding * 2);
          const titleHeight = 24;
          if (y + 50 > pageHeight - margin) { doc.addPage(); y = margin; }
          y += 15;
          let boxStartY = y;
          doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
          doc.text('Terms & Conditions', margin + boxPadding, y + 14); y += titleHeight;
          doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
          let currentPageBoxStartY = boxStartY;
          termsLines.forEach(line => {
            if (y + lineHeight > pageHeight - margin) {
              doc.setDrawColor(0, 0, 0);
              doc.rect(margin, currentPageBoxStartY, pageWidth - margin * 2, y - currentPageBoxStartY + boxPadding, 'S');
              doc.addPage(); y = margin; currentPageBoxStartY = y;
              doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
              doc.text('Terms & Conditions (continued)', margin + boxPadding, y + 14); y += titleHeight;
              doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
            }
            doc.text(line, margin + boxPadding, y); y += lineHeight;
          });
          doc.setDrawColor(0, 0, 0);
          doc.rect(margin, currentPageBoxStartY, pageWidth - margin * 2, y - currentPageBoxStartY + boxPadding, 'S');
        }

        if (!cancelled) {
          const blob = doc.output('blob');
          const file = new File([blob], `${poNumber}.pdf`, { type: 'application/pdf' });
          setAutoPOFile(file);
        }
      } catch (e) {
        console.error('Auto PO PDF generation failed:', e);
      }
    })();

    return () => { cancelled = true; };
  }, [open, poNumber, companyId]);
  const hasProducts  = pendingItems.some(t => t._type === 'products');
  const docTypes     = hasProducts ? [...BASE_DOCS, ...PRODUCT_EXTRA_DOCS] : BASE_DOCS;

  const needsExpiry = (tx) => tx._type === 'products';

  const canConfirm = pendingItems.every(tx => {
    const lots = itemLots[tx.id] || [];
    return lots.length > 0 && lots.every(lot => {
      if (!lot.lotNumber.trim() || !lot.batchNumber.trim()) return false;
      if (tx._type === 'products') {
        if (!lot._configId) return false;
        if (!lot.expiryDate) return false;
        if (!lot.mfgDate) return false;
        if (!(parseFloat(lot.unitQty) > 0 || parseFloat(lot.caseQty) > 0 || parseFloat(lot.palletQty) > 0)) return false;
      } else {
        if (needsExpiry(tx) && !lot.expiryDate) return false;
      }
      return true;
    });
  });

  const setDoc = (key, file) => setDocuments(prev =>
    file ? { ...prev, [key]: file }
         : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key))
  );

  const updateLot = (txId, lotIdx, field, value) => {
    setItemLots(prev => ({
      ...prev,
      [txId]: prev[txId].map((l, i) => i === lotIdx ? { ...l, [field]: value } : l),
    }));
  };

  const updateLotMulti = (txId, lotIdx, fields) => {
    setItemLots(prev => ({
      ...prev,
      [txId]: prev[txId].map((l, i) => i === lotIdx ? { ...l, ...fields } : l),
    }));
  };

  const addLot = (tx) => {
    const emptyLot = tx._type === 'products'
      ? { lotNumber: '', batchNumber: '', quantity: '', expiryDate: '', unitQty: '', caseQty: '', palletQty: '', mfgDate: '',
          _configId: '', _configName: '', _unitsPerCase: 1, _casesPerPallet: 1, _enableCases: true, _enablePallets: false }
      : { lotNumber: '', batchNumber: '', quantity: '', expiryDate: '' };
    setItemLots(prev => ({
      ...prev,
      [tx.id]: [...(prev[tx.id] || []), emptyLot],
    }));
  };

  const removeLot = (txId, lotIdx) => {
    setItemLots(prev => ({
      ...prev,
      [txId]: prev[txId].filter((_, i) => i !== lotIdx),
    }));
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError(null);
    setProgress(0);

    // Flatten to (tx, lot) pairs with per-tx lot index tracking
    const pairs = [];
    for (const tx of pendingItems) {
      const lots = itemLots[tx.id] || [];
      lots.forEach((lot, lotIndex) => {
        pairs.push({ tx, lot, lotIndex, totalLots: lots.length });
      });
    }

    try {
      // Pre-compute per-tx: ordered qty vs total delivered qty to detect partial delivery
      const txDeliveryInfo = {};
      for (const tx of pendingItems) {
        const lots = itemLots[tx.id] || [];
        let orderedQty = 0;
        if (tx._type === 'chemicals') orderedQty = parseFloat(tx.unit_quantity ?? tx.total_quantity) || 0;
        else if (tx._type === 'products') orderedQty = parseFloat(tx.quantity_change ?? tx.total_base_units) || 0;
        else orderedQty = parseFloat(tx.quantity) || 0;

        const totalDelivered = lots.reduce((sum, l) => {
          if (tx._type === 'products') {
            const upc = parseInt(l._unitsPerCase) || 1;
            const cpp = parseInt(l._casesPerPallet) || 1;
            return sum + (parseInt(l.unitQty) || 0) + (parseInt(l.caseQty) || 0) * upc + (parseInt(l.palletQty) || 0) * cpp * upc;
          }
          return sum + (parseFloat(l.quantity) || 0);
        }, 0);

        txDeliveryInfo[tx.id] = { orderedQty, totalDelivered, isFullDelivery: totalDelivered >= orderedQty };
      }

      for (let i = 0; i < pairs.length; i++) {
        const { tx, lot, lotIndex, totalLots } = pairs[i];
        const config = getInventoryConfig(tx._type);
        const { isFullDelivery, orderedQty, totalDelivered } = txDeliveryInfo[tx.id] || { isFullDelivery: true, orderedQty: 0, totalDelivered: 0 };

        const location = { id: tx.location_id || tx.to_location_id };
        const lotData  = {
          lotNumber:       lot.lotNumber   || '',
          batchNumber:     lot.batchNumber || '',
          quantity:        parseFloat(lot.quantity) || 0,
          // Product unit config fields
          unitQty:         lot.unitQty,
          caseQty:         lot.caseQty,
          palletQty:       lot.palletQty,
          mfgDate:         lot.mfgDate,
          _configId:       lot._configId,
          _unitsPerCase:   lot._unitsPerCase,
          _casesPerPallet: lot._casesPerPallet,
          _enableCases:    lot._enableCases,
          _enablePallets:  lot._enablePallets,
        };
        const item = { id: tx.raw_material_id || tx[config?.itemIdField] || '' };

        if (tx._type === 'chemicals') {
          const { data: rm } = await supabase
            .from('raw_materials').select('primary_unit_quantity').eq('id', item.id).single();
          if (rm) item.primary_unit_quantity = rm.primary_unit_quantity;
        }

        // Auto-include PO PDF unless user already uploaded one (only on first lot)
        const finalDocs = autoPOFile && !documents.po
          ? { ...documents, po: autoPOFile }
          : documents;

        await confirmPODelivery(companyId, tx._type, config, tx.id, {
          location,
          lot:                lotData,
          item,
          expiryDate:         lot.expiryDate         || null,
          actualDeliveryDate: actualDate              || null,
          documents:          finalDocs,
          taxAmount:          taxAmount               || null,
          shippingCost:       shippingCost            || null,
          invoiceDueDate:     invoiceDueDate          || null,
          invoiceNumber:      invoiceNumber           || null,
          invoiceDate:        invoiceDate             || null,
          variantId:          tx._type === 'products' ? (selectedVariants[tx.id] || null) : null,
        }, { lotIndex, totalLots, isFullDelivery, orderedQty, totalDelivered });

        setProgress(Math.round(((i + 1) / pairs.length) * 100));
      }

      // Mark the PO as received
      if (poNumber && poNumber !== '—') {
        await supabase
          .from('vendor_PO')
          .update({ status: 'received' })
          .eq('company_id', companyId)
          .eq('vendor_PO_number', poNumber);
      }

      onSuccess?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!transactions.length) return null;

  return (
    <>
    <Dialog open={open} onClose={!submitting ? onClose : undefined} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
      sx={{ '& .MuiBackdrop-root': { bgcolor: 'rgba(0,0,0,0.7)' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}` }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>Confirm Delivery</Typography>
          <Typography variant="body2" color="text.secondary">PO #{poNumber} — {pendingItems.length} item{pendingItems.length !== 1 ? 's' : ''}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={submitting}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {/* Shared top fields */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ mb: 2.5, pt: 3 }}>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Actual Delivery Date"
                value={actualDate ? dayjs(actualDate) : null}
                onChange={v => setActualDate(v ? v.format('YYYY-MM-DD') : '')}
                slotProps={{
                  textField: {
                    size: 'small', fullWidth: true, sx: tfSx(theme),
                    InputLabelProps: { shrink: true },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth size="small" label="Invoice Number (optional)"
                value={invoiceNumber}
                onChange={e => setInvoiceNumber(e.target.value)}
                sx={tfSx(theme)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Invoice Date (optional)"
                value={invoiceDate ? dayjs(invoiceDate) : null}
                onChange={v => setInvoiceDate(v ? v.format('YYYY-MM-DD') : '')}
                slotProps={{
                  textField: {
                    size: 'small', fullWidth: true, sx: tfSx(theme),
                    InputLabelProps: { shrink: true },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Invoice Due Date (optional)"
                value={invoiceDueDate ? dayjs(invoiceDueDate) : null}
                onChange={v => setInvoiceDueDate(v ? v.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: tfSx(theme), InputLabelProps: { shrink: true } } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth size="small" label="Tax Amount (optional)" type="number"
                value={taxAmount}
                onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) setTaxAmount(v); }}
                inputProps={{ min: 0, step: '0.01' }}
                sx={tfSx(theme)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth size="small" label="Shipping Cost (optional)" type="number"
                value={shippingCost}
                onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) setShippingCost(v); }}
                inputProps={{ min: 0, step: '0.01' }}
                sx={tfSx(theme)}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        {/* Document uploads */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
            Upload Documents (Optional)
          </Typography>
          {autoPOFile && (
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
              PO #{poNumber} auto-attached
            </Typography>
          )}
        </Box>
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {docTypes.map(d => (
            <Grid item xs={12} sm={6} md={3} key={d.key}>
              <DocUploadField docKey={d.key} label={d.label} file={documents[d.key] || null} onChange={setDoc} />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 2.5 }} />

        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500, fontSize: '0.85rem' }}>
          Items to Confirm
        </Typography>

        {/* Per-item cards */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {pendingItems.map(tx => {
            const color     = TYPE_COLORS[tx._type] || '#666';
            const reqExpiry = needsExpiry(tx);
            const lots      = itemLots[tx.id] || [];

            return (
              <Box key={tx.id} sx={{ borderRadius: 2, border: `1px solid ${alpha(color, 0.25)}`, overflow: 'hidden' }}>
                {/* Item header */}
                <Box sx={{ px: 2.5, py: 1.5, bgcolor: alpha(color, 0.06), borderBottom: `1px solid ${alpha(color, 0.12)}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Chip
                    label={TYPE_LABELS[tx._type] || tx._type}
                    size="small"
                    sx={{ bgcolor: alpha(color, 0.15), color, fontWeight: 500, height: 22, fontSize: '0.7rem' }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{getItemName(tx)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Original Qty: {getOriginalQty(tx)}{tx._type === 'chemicals' && tx.raw_materials?.primary_unit_type ? ` ${tx.raw_materials.primary_unit_type}` : ''}
                    {tx._type === 'chemicals' && tx.raw_materials?.primary_unit_quantity && tx.raw_materials?.primary_unit_measure
                      ? ` (${(getOriginalQty(tx) * tx.raw_materials.primary_unit_quantity).toLocaleString(undefined, { maximumFractionDigits: 3 })} ${tx.raw_materials.primary_unit_measure})`
                      : ''}
                  </Typography>
                </Box>

                {/* Lot rows */}
                <Box sx={{ p: 2.5 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {lots.map((lot, lotIdx) => {
                      // Chemical unit-of-measure info
                      const purchaseUnit   = tx.raw_materials?.primary_unit_type    || '';
                      const primaryUnit    = tx.raw_materials?.primary_unit_measure || '';
                      const convFactor     = parseFloat(tx.raw_materials?.primary_unit_quantity) || null;
                      const isChemicalTx   = tx._type === 'chemicals';
                      const computedTotal  = isChemicalTx && convFactor && lot.quantity
                        ? ((parseFloat(lot.quantity) || 0) * convFactor).toLocaleString(undefined, { maximumFractionDigits: 4 })
                        : null;
                      const qtyLabel = isChemicalTx && purchaseUnit
                        ? `Quantity (${purchaseUnit})`
                        : 'Quantity';

                      return (
                      <Box key={lotIdx} sx={{ mb: lotIdx < lots.length - 1 ? 2 : 0 }}>
                        {lots.length > 1 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.75rem' }}>
                              Lot Entry #{lotIdx + 1}
                            </Typography>
                            <IconButton size="small" onClick={() => removeLot(tx.id, lotIdx)}
                              sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        )}
                        {tx._type === 'products' ? (
                          <>
                            {/* Variant selector */}
                            {(variantOptions[tx.id] || []).length > 0 && (
                              <FormControl size="small" fullWidth sx={{ mb: 1.5, ...tfSx(theme) }}>
                                <InputLabel>Variant (optional)</InputLabel>
                                <Select
                                  label="Variant (optional)"
                                  value={selectedVariants[tx.id] || ''}
                                  onChange={e => setSelectedVariants(prev => ({ ...prev, [tx.id]: e.target.value || null }))}
                                >
                                  <MenuItem value=""><em>None</em></MenuItem>
                                  {(variantOptions[tx.id] || []).map(v => (
                                    <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            {/* Unit Config selector */}
                            <Box sx={{ mb: 1.5 }}>
                              <Button
                                variant="outlined" size="small"
                                startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
                                onClick={() => { setConfigDialogTarget({ txId: tx.id, lotIdx }); setConfigSearch(''); setConfigDialogOpen(true); }}
                                sx={{
                                  borderRadius: 2, textTransform: 'none', fontSize: '0.8rem',
                                  border: `1px solid ${lot._configId ? alpha(theme.palette.primary.main, 0.4) : alpha(theme.palette.error.main, 0.5)}`,
                                  color: lot._configId ? 'primary.main' : 'error.main',
                                }}
                              >
                                {lot._configName || 'Select Unit Configuration *'}
                              </Button>
                              {!lot._configId && <Typography variant="caption" color="error" sx={{ ml: 1 }}>Required</Typography>}
                            </Box>
                            {/* Qty fields (only shown after config selected) */}
                            {lot._configId && (
                              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                                <Grid item xs={6} sm={lot._enablePallets ? 4 : (lot._enableCases ? 6 : 12)}>
                                  <TextField fullWidth size="small" label="Units" type="number"
                                    value={lot.unitQty}
                                    onChange={e => updateLot(tx.id, lotIdx, 'unitQty', e.target.value)}
                                    inputProps={{ min: 0 }} sx={tfSx(theme)} />
                                </Grid>
                                {lot._enableCases && (
                                  <Grid item xs={6} sm={lot._enablePallets ? 4 : 6}>
                                    <TextField fullWidth size="small" label="Cases" type="number"
                                      value={lot.caseQty}
                                      onChange={e => updateLot(tx.id, lotIdx, 'caseQty', e.target.value)}
                                      inputProps={{ min: 0 }}
                                      helperText={`${lot._unitsPerCase} units/case`}
                                      sx={tfSx(theme)} />
                                  </Grid>
                                )}
                                {lot._enablePallets && (
                                  <Grid item xs={6} sm={4}>
                                    <TextField fullWidth size="small" label="Pallets" type="number"
                                      value={lot.palletQty}
                                      onChange={e => updateLot(tx.id, lotIdx, 'palletQty', e.target.value)}
                                      inputProps={{ min: 0 }}
                                      helperText={`${lot._casesPerPallet} cases/pallet`}
                                      sx={tfSx(theme)} />
                                  </Grid>
                                )}
                              </Grid>
                            )}
                            {/* Lot / Batch / Mfg / Expiry */}
                            <Grid container spacing={1.5}>
                              <Grid item xs={6}>
                                <TextField fullWidth size="small" label="Lot Number *"
                                  value={lot.lotNumber}
                                  onChange={e => updateLot(tx.id, lotIdx, 'lotNumber', e.target.value)}
                                  error={!lot.lotNumber.trim()} helperText={!lot.lotNumber.trim() ? 'Required' : ''}
                                  sx={tfSx(theme)} />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField fullWidth size="small" label="Batch Number *"
                                  value={lot.batchNumber}
                                  onChange={e => updateLot(tx.id, lotIdx, 'batchNumber', e.target.value)}
                                  error={!lot.batchNumber.trim()} helperText={!lot.batchNumber.trim() ? 'Required' : ''}
                                  sx={tfSx(theme)} />
                              </Grid>
                              <Grid item xs={6}>
                                <DatePicker
                                  label={tx._type === 'products' ? 'Mfg Date *' : 'Mfg Date (optional)'}
                                  value={lot.mfgDate ? dayjs(lot.mfgDate) : null}
                                  onChange={v => updateLot(tx.id, lotIdx, 'mfgDate', v ? v.format('YYYY-MM-DD') : '')}
                                  slotProps={{ textField: { size: 'small', fullWidth: true, sx: tfSx(theme), InputLabelProps: { shrink: true }, error: tx._type === 'products' && !lot.mfgDate, helperText: tx._type === 'products' && !lot.mfgDate ? 'Required' : '' } }}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <DatePicker
                                  label="Expiry Date *"
                                  value={lot.expiryDate ? dayjs(lot.expiryDate) : null}
                                  onChange={v => updateLot(tx.id, lotIdx, 'expiryDate', v ? v.format('YYYY-MM-DD') : '')}
                                  slotProps={{
                                    textField: {
                                      size: 'small', fullWidth: true, required: true,
                                      error: !lot.expiryDate, helperText: !lot.expiryDate ? 'Required' : '',
                                      sx: tfSx(theme), InputLabelProps: { shrink: true },
                                    }
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </>
                        ) : (
                          <Grid container spacing={1.5}>
                            <Grid item xs={6} md={reqExpiry ? 2.5 : 3}>
                              <TextField
                                fullWidth size="small" label="Lot Number *"
                                value={lot.lotNumber}
                                onChange={e => updateLot(tx.id, lotIdx, 'lotNumber', e.target.value)}
                                error={!lot.lotNumber.trim()}
                                helperText={!lot.lotNumber.trim() ? 'Required' : ''}
                                sx={tfSx(theme)}
                              />
                            </Grid>
                            <Grid item xs={6} md={reqExpiry ? 2.5 : 3}>
                              <TextField
                                fullWidth size="small" label="Batch Number *"
                                value={lot.batchNumber}
                                onChange={e => updateLot(tx.id, lotIdx, 'batchNumber', e.target.value)}
                                error={!lot.batchNumber.trim()}
                                helperText={!lot.batchNumber.trim() ? 'Required' : ''}
                                sx={tfSx(theme)}
                              />
                            </Grid>
                            <Grid item xs={6} md={isChemicalTx && computedTotal ? 2.5 : 3}>
                              <TextField
                                fullWidth size="small" label={qtyLabel} type="number"
                                value={lot.quantity}
                                onChange={e => updateLot(tx.id, lotIdx, 'quantity', e.target.value)}
                                inputProps={{ min: 0, step: 'any' }}
                                helperText={isChemicalTx && convFactor && purchaseUnit && primaryUnit
                                  ? `1 ${purchaseUnit} = ${convFactor}${primaryUnit}`
                                  : ''}
                                sx={tfSx(theme)}
                              />
                            </Grid>
                            {isChemicalTx && computedTotal && (
                              <Grid item xs={6} md={2.5} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <TextField
                                  fullWidth size="small" label={`Total (${primaryUnit || 'base'})`}
                                  value={computedTotal}
                                  InputProps={{ readOnly: true }}
                                  sx={{ ...tfSx(theme), '& .MuiOutlinedInput-root': { ...tfSx(theme)['& .MuiOutlinedInput-root'], bgcolor: 'rgba(0,0,0,0.03)' } }}
                                  helperText="Auto-calculated"
                                />
                              </Grid>
                            )}
                            {isChemicalTx && (
                              <Grid item xs={6} md={2.5}>
                                <DatePicker
                                  label="Expiry Date (optional)"
                                  value={lot.expiryDate ? dayjs(lot.expiryDate) : null}
                                  onChange={v => updateLot(tx.id, lotIdx, 'expiryDate', v ? v.format('YYYY-MM-DD') : '')}
                                  slotProps={{
                                    textField: {
                                      size: 'small', fullWidth: true,
                                      sx: tfSx(theme), InputLabelProps: { shrink: true },
                                    }
                                  }}
                                />
                              </Grid>
                            )}
                            {!isChemicalTx && (
                              <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }} />
                            )}
                          </Grid>
                        )}
                        {lotIdx < lots.length - 1 && <Divider sx={{ mt: 2, borderStyle: 'dashed' }} />}
                      </Box>
                      );
                    })}
                  </LocalizationProvider>

                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                    onClick={() => addLot(tx)}
                    sx={{ mt: 1.5, color, fontWeight: 500, fontSize: '0.8rem', '&:hover': { bgcolor: alpha(color, 0.08) } }}
                  >
                    Add Another Lot
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>

        {submitting && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Confirming items... {progress}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.grey[200], 0.8)}` }}>
        <Button onClick={onClose} disabled={submitting} sx={{ borderRadius: 2 }}>Cancel</Button>
        <Button
          variant="contained" onClick={handleConfirm}
          disabled={submitting || !canConfirm}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
          color="success"
          sx={{ borderRadius: 2, px: 3, fontWeight: 500 }}
        >
          {submitting ? 'Confirming...' : `Confirm All ${pendingItems.length} Items`}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Unit Configuration Selection Dialog */}
    <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 500 }}>Select Unit Configuration</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth size="small" placeholder="Search configurations..."
          value={configSearch} onChange={e => setConfigSearch(e.target.value)}
          sx={{ mb: 1 }}
        />
        <List disablePadding>
          {unitConfigs
            .filter(c => c.name.toLowerCase().includes(configSearch.toLowerCase()))
            .map(cfg => (
              <ListItem key={cfg.id} disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (configDialogTarget) {
                      const { txId, lotIdx } = configDialogTarget;
                      updateLotMulti(txId, lotIdx, {
                        _configId:      cfg.id,
                        _configName:    cfg.name,
                        _unitsPerCase:  cfg.units_per_case   || 1,
                        _casesPerPallet:cfg.cases_per_pallet || 1,
                        _enableCases:   cfg.enable_cases  ?? true,
                        _enablePallets: cfg.enable_pallets ?? false,
                      });
                    }
                    setConfigDialogOpen(false);
                  }}
                  sx={{ borderRadius: 2, mb: 0.5 }}
                >
                  <ListItemText
                    primary={cfg.name}
                    secondary={`${cfg.units_per_case || 1} units/case${cfg.enable_pallets ? ` · ${cfg.cases_per_pallet || 1} cases/pallet` : ''}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          {unitConfigs.filter(c => c.name.toLowerCase().includes(configSearch.toLowerCase())).length === 0 && (
            <ListItem><ListItemText primary="No configurations found" /></ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
